/**
 * Licensed Materials - Property of IBM
 * IBM Watson Analytics (C) Copyright IBM Corp. 2018, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
/**
 * The SlotEditorA11YHandler is responsible for handling any slot-editor-specific A11Y handling.
 * Specifically, it handles selection of an item as a drag source and calling back to complete a move on enter.
 */
import $ from 'jquery';
import { A11YUtils }  from './lib/@waca/a11y/src/js';

class SlotEditorA11YHandler {

	/**
	 * Constructor
	 */
	constructor() {}

	/**
	 * Handle the case the user hits space on the focused item
	 * @param event - the keydown event
	 * @param {object} handlers , A JSON object that holds mappings of slot handlers
	 * @param [callback] - callback to execute if the key is not space
	 */
	handleKeyDown(event, handlers, callback = null) {
		if (event.key === ' ') {
			//Hitting a space should allow the focus item to be stored (for 'dropping' on enter.)
			this._sourceSelection = handlers.slotDataItemHandler;
			//TODO: addClass/removeClass should be removed when we convert the widget model to mobx
			$(this.sourceNode).removeClass('selected');
			this.sourceNode = event.target;
			$(this.sourceNode).addClass('selected');
			event.preventDefault(); // prevent the the browser to scroll page down
		} else {
			this.handleOpenTypeInDropdown(event, callback);
		}
	}

	/**
	 * Handle the case the user do delete or paste
	 * @param event - the keydown event
	 * @param {object} handlers , A JSON object that holds mappings of slot handlers
	 */
	handleKeyUp(event, handlers) {
		if (event.key === 'Delete' || event.key === 'Backspace') {
			if ($(event.target).hasClass('slotEditorItem')) {
				this.deleteSlotItem(handlers.slotDataItemHandler);
				event.preventDefault();
			}
		} else if (A11YUtils.isPaste(event)) {
			this.slotHandleKeyUp(event, handlers.slotDataItemHandler);
			event.preventDefault();
		}
	}

	/**
	 * Handle key down event specifically for local filter item
	 * @param event - the keydown event
	 * @param filterItemActionHandler - filterItemActionHandler
	 * @param options
	 * @param options.metaDataColumn - metaDataColumn
	 * @param options.filterId - filterId
	 */
	handleKeyUpForFilterItem(event, filterItemActionHandler, options) {
		if (event.key === 'Delete' || event.key === 'Backspace') {
			if ($(event.target).hasClass('slotEditorItem')) {
				this._deleteFilterItem(filterItemActionHandler, options);
				event.preventDefault();
			}
		}
	}

	/**
	 * Handle 'enter' key event if no selection is made already
	 * @param {} event - the keydown event 
	 * @param {*} expandDropdown - callback to execute 
	 */
	handleOpenTypeInDropdown(event, expandDropdown) {
		if (!this._sourceSelection && event.key === 'Enter') {
			expandDropdown();
		}
	}

	/**
	 * Complete the action by calling the onEnter callback.
	 * @param event - the keypress event
	 * @param cbOnEnter - a callback function which should complete the event when a user hits enter.
	 * @param options - pass any options on to the callback function as an argument (in the form the callback expects)
	 */
	handleKeyPress(event, cbOnEnter, options) {
		if(this._sourceSelection && event.key === 'Enter') {
			//TODO: addClass/removeClass should be removed when we convert the widget model to mobx
			$(this.sourceNode).removeClass('selected');
			cbOnEnter(this._sourceSelection, options);
			this._sourceSelection = null;
			event.preventDefault();
		}
	}

	/**
	 * Handles adding data item from tree and Ctrl + V to slot area
	 * @param event
	 * @param slotDataItemHandler
	 */
	slotHandleKeyUp(event, slotDataItemHandler){
		// ctrl + V
		if(A11YUtils.isPaste(event)) {
			const addAfter = !(event && event.target && event.target.classList.contains('slotEditorItem'));
			slotDataItemHandler.onDropA11Y(addAfter);
		}
	}

	/**
	 * Handles adding data item from tree and Ctrl + V to context filters
	 * @param event
	 * @param node
	 * @param dropSlotItemHandler
	 * @param filterItemActionHandler
	 */
	filterHeaderHandleKeyUp(event, node, dropFilterItemHandler, filterItemActionHandler){
		// ctrl + V
		if(A11YUtils.isPaste(event)) {
			filterItemActionHandler.onDropA11Y(node);
		}
	}

	deleteSlotItem(slotDataItemHandler) {
		return slotDataItemHandler.getActions()
			.then((actions) => {
				const deleteAction = actions.find((action) => action && action.name === 'delete');
				deleteAction && deleteAction.actions.apply();
			});
	}

	_deleteFilterItem(filterItemActionHandler, options) {
		return filterItemActionHandler.getActions(options.metadataColumn, options.filterId)
			.then((actions) => {
				const deleteAction = actions.find((action) => action.deleteLocalFilter !== undefined);
				if (deleteAction) {
					deleteAction.deleteLocalFilter();
				}
			});
	}
}
export default SlotEditorA11YHandler;



// WEBPACK FOOTER //
// ./src/js/SlotEditorA11YHandler.js